import { lockBody, unlockBody, BodyLockState } from '../helpers/lockBody';
import { waitForImages } from '../helpers/waitForImages';

// Static strings
const openMenuClass = 'nav-menu-is-open';
const navLoadedClass = 'nav-has-loaded';
const menuSlideStateAttribute = 'data-nav-expanded';
const navId = "navContainer";
const menuId = "nav-mobile-menu";
const breadCrumbId = "breadcrumbs";
const megaNavOpenAttribute = 'data-mega-nav-open';
const searchId = "navSearch";
class MainNavigation {
    htmlElement: HTMLElement;
    container: HTMLElement;
    menuContainer: HTMLElement;
    menuTrigger: HTMLButtonElement;
    menuPanel: HTMLElement;
    menuFocusableChildren: Array<HTMLButtonElement | HTMLAnchorElement | HTMLInputElement>;
    menuIsOpen: boolean;
    navIsShowing: boolean;
    existingBodyStyles: BodyLockState | null;
    lastScrollPosition: number;
    hasLoaded: boolean;
    onPageAnchorLinks: HTMLAnchorElement[];
    navSearchCloseBtn: null | HTMLElement;
    navItemTriggers: HTMLButtonElement[];
    subNavsOpen: boolean;
    breadcrumbs: HTMLElement;
    searchIsOpen: boolean;
    searchTrigger: HTMLElement;
    searchPanel: HTMLElement;
    searchInput: HTMLInputElement;
    scrollLocked: boolean;

    constructor(navigation: HTMLElement) {
        this.htmlElement = document.documentElement;
        this.container = navigation;
        this.menuTrigger = this.container.querySelector(`[aria-controls="${menuId}"]`) as HTMLButtonElement;
        this.menuPanel = document.querySelector(`#${menuId}`) as HTMLElement;
        this.menuFocusableChildren = [].slice.call(this.menuPanel.querySelectorAll('button, a, input'));
        this.breadcrumbs = document.querySelector(`#${breadCrumbId}`) as HTMLElement;
        this.menuIsOpen = false;
        this.navIsShowing = true;
        this.hasLoaded = false;
        this.existingBodyStyles = null;
        this.navSearchCloseBtn = null;
        this.searchPanel = document.querySelector(`#${searchId}`) as HTMLElement;
        this.searchIsOpen = false;
        this.searchTrigger = this.container.querySelector('[data-search-trigger]') as HTMLElement;
        this.searchInput = this.searchPanel?.querySelector('input[name="q"]') as HTMLInputElement;
        this.navItemTriggers = [].slice.call(this.container.querySelectorAll('[data-nav-button]'));
        // Intercept on-page links so that the offset recalculates before the browser reaches it
        // This makes sure that the target is always comfortably visible below the nav
        this.onPageAnchorLinks = [].slice.call(document.querySelectorAll('a[href^="#"]'));
        this.onPageAnchorLinks.forEach(link => {
            link.addEventListener('click', (e: Event) => {
                e.preventDefault();
                const hash = link.getAttribute('href');
                const target = document.querySelector(hash) as HTMLElement;
                if (target) {
                    if (this.navIsShowing && target.offsetTop > window.pageYOffset) {
                        this.slideUp();
                    }
                    else if (!this.navIsShowing && target.offsetTop < window.pageYOffset) {
                        this.slideDown();
                    }
                    setTimeout(() => {
                        target.scrollIntoView(true);
                        target.focus();
                        window.location.hash = hash;
                    }, 0);
                }
            })
        })
        this.navItemTriggers.forEach(trigger => {
            trigger.addEventListener('click', () => this.handleTriggerClick(trigger));
        });
        this.subNavsOpen = false;
        this.scrollLocked = false;
        this.setCssVariables();
    }
    init = () => {
        this.menuTrigger.addEventListener('click', () => {
            if (this.menuIsOpen) {
                this.closeMenu();
            }
            else {
                if (this.searchIsOpen) {
                    this.closeSearch();
                }
                this.openMenu();
            }
        });

        this.searchTrigger.addEventListener('click', () => {

            if (this.menuIsOpen) {
                this.closeMenu();
                this.openSearch();
            }
            else if (this.searchIsOpen) {
                this.closeSearch();
            }
            else {
                this.closeAllSubMenus();
                this.openSearch();
            }
        });

        document.body.addEventListener('keyup', (e: KeyboardEvent) => {
            if (e.which === 27) {
                if (this.menuIsOpen) { // Escape key
                    this.closeMenu();
                    this.menuTrigger.focus();
                }
                if (this.subNavsOpen) { // Escape key
                    this.closeAllSubMenus();
                }
            }
            if (e.which == 9) { // Tab key. Show nav if tabbed into, or close menu if tabbed out of.
                const focusInNav = this.container.contains(document.activeElement);
                if (focusInNav && !this.navIsShowing) {
                    this.slideDown();
                }
                if (!focusInNav && this.menuIsOpen) {
                    this.closeMenu();
                }
            }
        });

        // Setup on page load
        this.closeMenu();
        this.setPageOffset();
        this.slideDown();

        // wait for the site logo to load, then set the nav height again
        waitForImages(this.container)
            .then(() => {
                this.setPageOffset();
            });
    }

    closeAllSubMenus = () => {
        const openSubMenus = this.navItemTriggers.filter(navItemTrigger => navItemTrigger.getAttribute('aria-expanded') === 'true');
        openSubMenus.forEach(subMenuTrigger => {
            this.closeSubnavPanel(subMenuTrigger);
        });
        this.subNavsOpen = false;
    }

    detectOutsideSubmenuClick = (e: MouseEvent) => {
        if (this.subNavsOpen && !this.menuIsOpen) {//If a submenu is open but it's not in a mobile menu
            if (!(e.target as HTMLElement).closest(`[data-nav-sub]`)) {
                this.closeAllSubMenus();
            }
        }
    }

    openSubnavPanel = (trigger: HTMLButtonElement) => {
        this.closeAllSubMenus();
        const panelToOpen = document.querySelector(`#${trigger.getAttribute('aria-controls')}`) as HTMLElement;
        panelToOpen.inert = false;
        trigger.setAttribute('aria-expanded', 'true');
        this.subNavsOpen = true;
        document.addEventListener("click", this.detectOutsideSubmenuClick.bind(this));
        this.htmlElement.setAttribute(megaNavOpenAttribute, 'true');
    }

    closeSubnavPanel = (trigger: HTMLButtonElement) => {
        const panelToClose = document.querySelector(`#${trigger.getAttribute('aria-controls')}`) as HTMLElement;
        panelToClose.inert = true;
        trigger.setAttribute('aria-expanded', 'false');
        this.subNavsOpen = false;
        document.removeEventListener("click", this.detectOutsideSubmenuClick.bind(this));
        this.htmlElement.setAttribute(megaNavOpenAttribute, 'false');
    }

    handleTriggerClick = (trigger: HTMLButtonElement) => {
        const triggerIsOpen = trigger.getAttribute('aria-expanded') == 'true';
        if (!triggerIsOpen) {
            if (this.searchIsOpen) {
                this.closeSearch();
            }
            this.openSubnavPanel(trigger);
        }
        else {
            this.closeSubnavPanel(trigger);
        }
    }

    slideDown = () => {
        this.navIsShowing = true;
        this.htmlElement.setAttribute(menuSlideStateAttribute, 'true');
        this.setCssVariables();
    }

    slideUp = () => {
        this.navIsShowing = false;
        this.htmlElement.setAttribute(menuSlideStateAttribute, 'false');
        this.setCssVariables();
    }

    openMenu = () => {
        this.menuPanel.setAttribute('aria-hidden', 'false');
        this.menuTrigger.setAttribute('aria-expanded', 'true');
        this.enableTabbableChildren();
        this.menuIsOpen = true;
        this.existingBodyStyles = lockBody();
        this.container.classList.add(openMenuClass);
    }

    closeMenu = () => {
        this.menuPanel.setAttribute('aria-hidden', 'true');
        this.menuTrigger.setAttribute('aria-expanded', 'false');
        this.disableTabbableChildren();
        this.menuIsOpen = false;
        if (this.existingBodyStyles) {
            unlockBody(this.existingBodyStyles);
            this.existingBodyStyles = null;
        }
        this.container.classList.remove(openMenuClass);
        this.setPageOffset();
    }

    openSearch = () => {
        this.searchIsOpen = true;
        this.searchPanel.setAttribute('aria-hidden', 'false');
        this.searchTrigger.setAttribute('aria-expanded', 'true');
        console.log(this.searchInput);
        setTimeout(() => {
            this.searchInput?.focus();
        }, 0);
    }

    closeSearch = () => {
        this.searchIsOpen = false;
        this.searchPanel.setAttribute('aria-hidden', 'true');
        this.searchTrigger.setAttribute('aria-expanded', 'false');
    }

    enableTabbableChildren = () => {
        this.menuFocusableChildren.forEach(child => {
            child.tabIndex = 0;
        })
    }

    disableTabbableChildren = () => {
        this.menuFocusableChildren.forEach(child => {
            child.tabIndex = -1;
        })
    }

    setPageOffset = () => {
        if (!this.hasLoaded) {
            this.container.classList.add(navLoadedClass);
            this.hasLoaded = true;
        }
        this.setCssVariables();
    }

    get currentNavHeight() {
        return this.container.offsetHeight;
    }

    scrollHandler = () => {
        // Skip scroll handling when body is locked (modal open)
        if (this.scrollLocked) {
            return;
        }
        if (this.hasLoaded) {
            const currentPosition = window.pageYOffset;
            const delta = currentPosition - this.lastScrollPosition;
            this.lastScrollPosition = currentPosition;
            if (this.navIsShowing) {
                if (delta > 0 && currentPosition > this.currentNavHeight && !this.menuIsOpen) {
                    this.slideUp();
                }
            }
            else if (delta < 0 || currentPosition < this.currentNavHeight) {
                this.slideDown();
            }
        }
    }

    getElementHeight = (element: HTMLElement) => {
        return element ? element.offsetHeight : 0;
    }

    resizeHandler = () => {
        this.setPageOffset();
    }

    setCssVariables = () => {
        document.documentElement.style.setProperty('--nav-clearance', `${this.navIsShowing ? this.currentNavHeight : this.currentNavHeight}px`);
        document.documentElement.style.setProperty('--breadcrumbs-offset', `${this.getElementHeight(this.breadcrumbs)}px`);
    }
}

let mainNavigation: MainNavigation = null;
const navContainer = document.querySelector(`#${navId}`) as HTMLElement;
if (navContainer) {
    mainNavigation = new MainNavigation(navContainer);
    (window as any).mainNavigation = mainNavigation;
}

export { mainNavigation };




